(function () {
    'use strict';

    const FIELD_ERROR_MESSAGES = {
        email: {
            valueMissing: 'Informe o e-mail cadastrado na sua conta.',
            typeMismatch: 'Informe um endereço de e-mail válido.',
        },
        password: {
            valueMissing: 'Informe sua senha.',
        },
    };

    function fieldErrorMessage(field) {
        const messages = FIELD_ERROR_MESSAGES[field.getAttribute('name')] ?? {};

        if (field.validity?.valueMissing) {
            return field.getAttribute('data-am-error-value-missing')
                ?? messages.valueMissing
                ?? 'Preencha este campo.';
        }

        if (field.validity?.typeMismatch) {
            return field.getAttribute('data-am-error-type-mismatch')
                ?? messages.typeMismatch
                ?? 'Revise o valor informado.';
        }

        if (field.validity?.tooShort) {
            return field.getAttribute('data-am-error-too-short')
                ?? 'Use mais caracteres neste campo.';
        }

        return 'Revise o valor informado.';
    }

    function fieldErrorElement(form, field) {
        const errorId = field.getAttribute('aria-errormessage');

        return errorId ? form.querySelector(`#${errorId}`) : null;
    }

    function showFieldError(form, field) {
        const error = fieldErrorElement(form, field);

        field.setAttribute('aria-invalid', 'true');

        if (error) {
            error.textContent = fieldErrorMessage(field);
            error.hidden = false;
        }
    }

    function clearFieldError(form, field) {
        const error = fieldErrorElement(form, field);

        field.removeAttribute('aria-invalid');

        if (error) {
            error.textContent = '';
            error.hidden = true;
        }
    }

    function enhanceLoginForm(form, windowObject = window) {
        if (!form || typeof form.querySelectorAll !== 'function') {
            return null;
        }

        const fields = Array.from(form.querySelectorAll(
            '[data-am-auth-field], [data-am-login-field]'
        ));
        const submitButton = form.querySelector('button[type="submit"]');
        const busyStatus = form.querySelector(
            '[data-am-auth-busy-status], [data-am-login-busy-status]'
        );
        let focusScheduled = false;
        let submitting = false;

        function scheduleFirstInvalidFocus() {
            if (focusScheduled) {
                return;
            }

            focusScheduled = true;
            const focusFirstInvalid = () => {
                focusScheduled = false;
                fields.find(field => field.validity && !field.validity.valid)?.focus();
            };

            if (typeof windowObject.setTimeout === 'function') {
                windowObject.setTimeout(focusFirstInvalid, 0);
            } else {
                focusFirstInvalid();
            }
        }

        function handleInvalid(event) {
            const field = event.target;

            if (!fields.includes(field)) {
                return;
            }

            event.preventDefault();
            showFieldError(form, field);
            scheduleFirstInvalidFocus();
        }

        function handleInput(event) {
            const field = event.target;

            if (!fields.includes(field)) {
                return;
            }

            if (field.validity?.valid) {
                clearFieldError(form, field);
            } else if (field.getAttribute('aria-invalid') === 'true') {
                showFieldError(form, field);
            }
        }

        function setBusy(busy) {
            submitting = busy;
            form.setAttribute('aria-busy', String(busy));

            if (submitButton) {
                if (busy) {
                    submitButton.setAttribute('aria-disabled', 'true');
                } else {
                    submitButton.removeAttribute('aria-disabled');
                }
            }

            if (busyStatus) {
                busyStatus.hidden = !busy;
            }
        }

        function handleSubmit(event) {
            if (event.defaultPrevented) {
                return;
            }

            if (submitting) {
                event.preventDefault();
                return;
            }

            setBusy(true);
        }

        function handlePageShow() {
            setBusy(false);
        }

        form.addEventListener('invalid', handleInvalid, true);
        form.addEventListener('input', handleInput);
        form.addEventListener('submit', handleSubmit);
        windowObject.addEventListener?.('pageshow', handlePageShow);

        return {
            destroy() {
                form.removeEventListener('invalid', handleInvalid, true);
                form.removeEventListener('input', handleInput);
                form.removeEventListener('submit', handleSubmit);
                windowObject.removeEventListener?.('pageshow', handlePageShow);
            },
        };
    }

    function focusErrorSummary(documentObject) {
        const summary = documentObject.querySelector?.(
            '[data-am-auth-error-summary], [data-am-login-error-summary]'
        );

        summary?.focus();

        return summary ?? null;
    }

    function initLoginPage(documentObject = document, windowObject = window) {
        const form = documentObject.querySelector?.(
            '[data-am-auth-form], [data-am-login-form]'
        );

        if (
            typeof form?.hasAttribute === 'function'
            && form.hasAttribute('data-react-managed')
        ) {
            return {form: null, summary: null};
        }

        return {
            form: enhanceLoginForm(form, windowObject),
            summary: focusErrorSummary(documentObject),
        };
    }

    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => initLoginPage(), {once: true});
        } else {
            initLoginPage();
        }
    }

    if (typeof module !== 'undefined' && module.exports) {
        module.exports = {enhanceLoginForm, focusErrorSummary, initLoginPage};
    }
})();
